import type { FilterState } from '@aviata/offers-filters-fns';
import type { flightEntity } from 'freedom-shared';

import { TIME_INTERVALS, DEFAULT_FILTER_LIMITS } from './config';
import type { FilterLimits } from './types';

const isRangeChanged = (current: number[], initial: number[]) =>
  current[0] > initial[0] || current[1] < initial[1];

const getFirstAndLastSegments = <T>(segments: Array<T>) => {
  const { length } = segments;
  return [segments[0], segments[length - 1]];
};

export const getFlightsRoutes = (offer: flightEntity.types.MappedOffer) =>
  offer.flights.map(({ segments }) => {
    const [first, last] = getFirstAndLastSegments(segments);
    return {
      dep: first.dep,
      arr: last.arr,
    };
  });

type FilterApplicationCheckInput = {
  state: FilterState;
  limits?: FilterLimits;
};

type FilterApplicationCheckFunction = ({
  state,
  limits,
}: FilterApplicationCheckInput) => boolean;

export const isPriceFilterApplied: FilterApplicationCheckFunction = ({
  state,
  limits = DEFAULT_FILTER_LIMITS,
}) => isRangeChanged(state.prices, limits.prices);

export const isTransferDurationApplied: FilterApplicationCheckFunction = ({
  state,
  limits = DEFAULT_FILTER_LIMITS,
}) =>
  isRangeChanged(state.flights[0].transfersDuration, limits.transferDuration);

export const isFlightDurationFilterApplied: FilterApplicationCheckFunction = ({
  state,
  limits = DEFAULT_FILTER_LIMITS,
}) => isRangeChanged(state.flights[0].duration, limits.flightDurationRange);

export const isAirlinesFilterApplied: FilterApplicationCheckFunction = ({
  state,
}) =>
  Object.values(state.airlines).some(Boolean) &&
  // @ts-expect-error isImplicitAllAirlines will always exist in FilterState
  (!state.isAllAirlines || state.isImplicitAllAirlines);

export const isFlightTimeFiltersApplied: FilterApplicationCheckFunction = ({
  state,
}) => {
  return state.flights.some(
    flight =>
      isRangeChanged(flight.arr.time, TIME_INTERVALS) ||
      isRangeChanged(flight.dep.time, TIME_INTERVALS),
  );
};

export const isTransferAmountFilterApplied: FilterApplicationCheckFunction = ({
  state,
}) => state?.flights[0].transfers.length > 0;

export const isWithoutNightTransferApplied: FilterApplicationCheckFunction = ({
  state,
}) =>
  // @ts-expect-error TODO: add local extended types
  state.isWithoutNightTransfer;

export const isTransferFiltersApplied: FilterApplicationCheckFunction = input =>
  isTransferAmountFilterApplied(input) ||
  isWithoutNightTransferApplied(input) ||
  isTransferDurationApplied(input);

const FILTER_APPLICATION_CHECKS = [
  isPriceFilterApplied,
  isAirlinesFilterApplied,
  isTransferDurationApplied,
  isFlightTimeFiltersApplied,
  isFlightDurationFilterApplied,
  isWithoutNightTransferApplied,
  isTransferAmountFilterApplied,
];

export const checkAppliedFilters = (input: FilterApplicationCheckInput) => {
  return FILTER_APPLICATION_CHECKS.reduce(
    (acc, curr) => {
      if (curr(input)) {
        acc.isAnyFilterApplied = true;
        acc.appliedFilterAmount++;
      }
      return acc;
    },
    { isAnyFilterApplied: false, appliedFilterAmount: 0 },
  );
};
